import { ArchiveBox, ArrowLeft, ArrowUturnLeft } from '@medusajs/icons';
import {
  Badge,
  Button,
  Checkbox,
  CuiLookup,
  type CuiLookupCompany,
  Input,
  Label,
  Select,
  Skeleton,
  Switch,
  Tabs,
  Text,
  Textarea,
  toast,
} from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { Field } from '../../components/field';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { useActiveCompany } from '../auth/use-session';
import { CustomerBilling } from '../billing/customer-billing';
import { lookupCompanyByCui } from '../companies/anaf.api';
import { companiesQueryOptions, companyDisplayName } from '../companies/companies.api';
import { CustomerDocuments } from '../documents/customer-documents';
import { nomenclatorQueryOptions } from '../settings/settings.api';
import { CustomerSubscriptions } from '../subscriptions/customer-subscriptions';
import { ArchiveCustomerDrawer } from './archive-customer-drawer';
import { FIELD_ANCHORS, validate } from './customer-form-rules';
import {
  CUSTOMER_TYPE_LIST,
  CUSTOMER_TYPES,
  CUSTOMERS_QUERY_KEY,
  type Customer,
  type CustomerInput,
  type CustomerType,
  createCustomer,
  customerDetailQueryOptions,
  customerQueryKey,
  hasAnafLookup,
  TRISTATE_OPTIONS,
  TRISTATE_UNKNOWN,
  toTristate,
  updateCustomer,
} from './customers.api';

/**
 * Fișa clientului: un formular care **se schimbă după felul clientului**.
 *
 * ## De ce pagină și nu sertar
 *
 * Firmele grupului au nouă câmpuri și încap într-un `Drawer`. Clientul are peste douăzeci,
 * grupate pe cinci teme, plus o listă de locații sub ele — într-un sertar ar fi o coloană de
 * derulat de un metru. Pagina are și un URL, deci fișa unui client se poate trimite prin
 * mesaj, ceea ce un sertar nu poate.
 *
 * ## De ce taburi
 *
 * Formularul de aici e **doar una din cele patru teme** ale unui client: detaliile lui,
 * documentele semnate, serviciile pe care le are și partea financiară. Puse cap la cap pe
 * verticală, ar da o pagină pe care nimeni n-o parcurge până jos — iar operatorul care caută
 * o locație ar derula peste douăzeci de câmpuri pe care nu le atinge.
 *
 * Antetul (titlul, „Înapoi la listă", „Salvează") stă **în afara** taburilor: `Salvează`
 * privește fișa întreagă, nu tabul deschis. Financiar are facturile clientului și „Emite
 * factură" din #186; până atunci a fost gol, ca să se vadă unde intră, nu ca să pară gata.
 *
 * ## Ce se schimbă cu tipul
 *
 * | | Persoană fizică | Persoană juridică | Firmă străină |
 * |---|---|---|---|
 * | Identificare | CNP + serie CI | CUI (căutabil la ANAF) + Nr. Reg. Com. | VAT ID + registru local |
 * | Reprezentant legal | — | da | da |
 * | Județ | da | da | **nu** |
 * | Țară | `RO` | `RO` | **obligatorie** |
 *
 * Ecranul **ascunde** câmpurile care nu se aplică; **serverul** le golește. Sunt două lucruri
 * diferite și amândouă trebuie să existe: un formular care doar ascunde ar lăsa CNP-ul în
 * bază la un client trecut din persoană fizică în firmă.
 */
export function CustomerScreen({
  customerId,
  tab,
  onTabChange,
}: {
  customerId?: string;
  /** Tabul deschis, citit din URL de rută. Vezi `customerTabSchema`. */
  tab: CustomerTab;
  onTabChange: (tab: CustomerTab) => void;
}) {
  const isNew = customerId === undefined;
  const detail = useQuery({
    ...customerDetailQueryOptions(customerId ?? ''),
    enabled: !isNew,
  });

  if (!isNew && detail.isPending) {
    return (
      <SectionContainer>
        <div className="flex flex-col gap-3 px-6 py-6">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      </SectionContainer>
    );
  }

  if (!isNew && detail.error) {
    return (
      <SectionContainer>
        <SectionEmpty tone="error">
          {detail.error instanceof Error ? detail.error.message : 'Fișa nu a putut fi citită.'}
        </SectionEmpty>
      </SectionContainer>
    );
  }

  return (
    <CustomerForm customer={detail.data?.customer ?? null} tab={tab} onTabChange={onTabChange} />
  );
}

/** Rândul, în forma în care îl editează formularul: `null` devine șir gol. */
function toInput(customer: Customer): CustomerInput {
  return {
    customerType: customer.customerType,
    companyId: customer.companyId,
    countryCode: customer.countryCode,
    nickname: customer.nickname,
    name: customer.name,
    clientProfileId: customer.clientProfileId ?? '',
    cnp: customer.cnp ?? '',
    cif: customer.cif ?? '',
    idCardNumber: customer.idCardNumber ?? '',
    tradeRegisterNo: customer.tradeRegisterNo ?? '',
    iban: customer.iban ?? '',
    bankName: customer.bankName ?? '',
    legalAddress: customer.legalAddress ?? '',
    city: customer.city ?? '',
    county: customer.county ?? '',
    postalCode: customer.postalCode ?? '',
    vatPayer: toTristate(customer.vatPayer),
    vatOnCollection: toTristate(customer.vatOnCollection),
    representativeName: customer.representativeName ?? '',
    representativeRole: customer.representativeRole ?? '',
    contactPerson: customer.contactPerson ?? '',
    email: customer.email ?? '',
    phoneSales: customer.phoneSales ?? '',
    phoneBilling: customer.phoneBilling ?? '',
    billingEmail: customer.billingEmail ?? '',
    phoneTechnical: customer.phoneTechnical ?? '',
    phoneOther: customer.phoneOther ?? '',
    notes: customer.notes ?? '',
    autoInvoicing: customer.autoInvoicing,
    detailedInvoice: customer.detailedInvoice,
    isSpecial: customer.isSpecial,
    bankStatementNames: customer.bankStatementNames ?? '',
    status: customer.status,
  };
}

function emptyInput(companyId: string): CustomerInput {
  return {
    // Persoana juridică e implicită pentru că e felul de client cu care se lucrează cel mai
    // des la un ISP de business — și e singurul cu precompletare de la ANAF.
    customerType: CUSTOMER_TYPES.company,
    companyId,
    countryCode: 'RO',
    nickname: '',
    name: '',
    clientProfileId: '',
    cnp: '',
    cif: '',
    idCardNumber: '',
    tradeRegisterNo: '',
    iban: '',
    bankName: '',
    legalAddress: '',
    city: '',
    county: '',
    postalCode: '',
    // „Nu se știe", nu „nu": un client nou chiar n-a fost verificat încă.
    vatPayer: TRISTATE_UNKNOWN,
    vatOnCollection: TRISTATE_UNKNOWN,
    representativeName: '',
    representativeRole: '',
    contactPerson: '',
    email: '',
    phoneSales: '',
    phoneBilling: '',
    billingEmail: '',
    phoneTechnical: '',
    phoneOther: '',
    notes: '',
    autoInvoicing: false,
    detailedInvoice: false,
    isSpecial: false,
    bankStatementNames: '',
    status: true,
  };
}

/**
 * Erorile de la server → câmpul căruia îi aparțin.
 *
 * Două surse, ca la firme: `validation_error` duce `issues: [{ path, message }]`, unde `path`
 * e chiar numele câmpului din contract; erorile de domeniu n-au `issues`, dar au un `code`
 * stabil. `customer_nickname_taken` e despre codul de client, deci ajunge sub el — nu într-un
 * toast pe care operatorul îl citește după ce s-a uitat deja la formular.
 */
const ERROR_CODE_FIELDS: Record<string, keyof CustomerInput> = {
  customer_nickname_taken: 'nickname',
  customer_reference_not_found: 'companyId',
};

function toFieldErrors(
  error: unknown,
  shape: CustomerInput,
): Partial<Record<keyof CustomerInput, string>> {
  if (!(error instanceof ApiError)) {
    return {};
  }

  const byCode = ERROR_CODE_FIELDS[error.code];
  if (byCode) {
    return { [byCode]: error.message };
  }

  const fields: Partial<Record<keyof CustomerInput, string>> = {};
  for (const issue of error.issues) {
    // `path` vine din contract, deci e deja numele câmpului. Ce nu se potrivește cu niciun
    // câmp din formular se ignoră aici și cade pe toast — mai bine văzut undeva decât nicăieri.
    if (issue.path in shape) {
      fields[issue.path as keyof CustomerInput] = issue.message;
    }
  }

  return fields;
}

/**
 * Câmpurile pe care le poate aduce ANAF.
 *
 * Nouă din douăzeci și ceva. Spre deosebire de ecranul firmelor, adresa nu intră într-un
 * singur câmp: fișa clientului are `legalAddress`, `city`, `county` și `postalCode`
 * separate, iar ANAF le întoarce deja despărțite — le-am lipi ca să le desparțim înapoi la
 * primul contract.
 *
 * ⚠️ **Starea firmei la ANAF nu e aici, dinadins** (activă / inactivă / radiată). E o
 * informație pentru operator în clipa căutării, nu o dată de contract: salvată pe fișă, ar
 * îmbătrâni tăcut — un „activă" din 2026 tipărit într-un act din 2028.
 */
const ANAF_FIELDS = [
  'name',
  'tradeRegisterNo',
  'legalAddress',
  'city',
  'county',
  'postalCode',
  'phoneOther',
  'vatPayer',
  'vatOnCollection',
] as const;

type AnafField = (typeof ANAF_FIELDS)[number];

/**
 * Tabul deschis, **ca parametru de căutare în URL**.
 *
 * `.catch('detalii')`: o adresă cu `?tab=` stricat — dintr-un link vechi, sau tastat —
 * deschide fișa pe primul tab, nu un ecran de eroare de rutare. Parametrul lipsește din URL
 * până când operatorul schimbă tabul; adresa unei fișe rămâne curată.
 *
 * Sursa de adevăr a valorilor e schema, iar lista de mai jos e tipată după ea: un tab
 * adăugat în listă și uitat aici n-ar trece de `validateSearch`, deci n-ar putea fi deschis
 * dintr-un link.
 *
 * ⚠️ **`semnare` se traduce, nu se refuză.** Tabul „Semnare electronică" a dispărut
 * (fondatorul, 2026-09-15), dar linkurile lui trăiesc mai departe în emailuri și în
 * favorite. Dus pe `.catch('detalii')`, un astfel de link ar fi deschis fișa pe primul tab,
 * adică nicăieri lângă ce căuta operatorul; documentele sunt acum locul unde se vede
 * semnarea, deci acolo duce. Vezi [#115](https://github.com/bplugaru/efx-pcrm/issues/115).
 */
export const customerTabSchema = z
  .preprocess(
    (value) => (value === 'semnare' ? 'documente' : value),
    z.enum(['detalii', 'documente', 'servicii', 'financiar']),
  )
  .catch('detalii');

export type CustomerTab = z.infer<typeof customerTabSchema>;

/** Ce validează rutele fișei (`/clienti/nou` și `/clienti/$customerId`). */
export const customerSearchSchema = z.object({ tab: customerTabSchema.default('detalii') });

/**
 * Temele fișei.
 *
 * Un tab fără nimic de arătat rămâne apăsabil, cu starea goală obișnuită („Niciun document.",
 * „Nicio factură."). Un tab dezactivat sau ascuns ar face ecranul să pară complet.
 *
 * **Fără promisiuni de fază** (fondatorul, 2026-08-18): tabul spune că nu e nimic acolo, nu
 * când o să fie. O dată livrată e o dată de respectat, iar ecranul n-are de unde s-o știe.
 */
const CUSTOMER_TABS: readonly { readonly value: CustomerTab; readonly label: string }[] = [
  { value: 'detalii', label: 'Detalii client' },
  { value: 'documente', label: 'Documente' },
  { value: 'servicii', label: 'Servicii' },
  { value: 'financiar', label: 'Financiar' },
];

function CustomerForm({
  customer,
  tab,
  onTabChange,
}: {
  customer: Customer | null;
  tab: CustomerTab;
  onTabChange: (tab: CustomerTab) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeCompany = useActiveCompany();
  const companies = useQuery(companiesQueryOptions());
  const profiles = useQuery(nomenclatorQueryOptions('profile-client'));

  const [form, setForm] = useState<CustomerInput>(
    customer ? toInput(customer) : emptyInput(activeCompany?.id ?? ''),
  );
  const [errors, setErrors] = useState<Partial<Record<keyof CustomerInput, string>>>({});
  /** Ce a întors ANAF pentru câmpurile pe care operatorul le scrisese deja. Vezi `applyAnaf`. */
  const [fromAnaf, setFromAnaf] = useState<Partial<Record<keyof CustomerInput, string>>>({});
  /** Câmpul spre care se derulează după un refuz. Vezi `showErrors`. */
  const [focusField, setFocusField] = useState<string | null>(null);
  /** Sertarul de arhivare. Deschis peste fișă, nu pe alt ecran: decizia se ia uitându-te la ea. */
  const [archiveOpen, setArchiveOpen] = useState(false);
  /**
   * Clientul e arhivat — adică nu se mai lucrează cu el (#111).
   *
   * Taburile îl primesc ca să nu mai ofere acțiuni care oricum se refuză pe server:
   * generarea unui document ar consuma un număr din registru, iar un serviciu nou ar fi
   * activ pe un client inactiv.
   */
  const isArchived = customer?.archivedAt != null;

  useEffect(() => {
    if (customer) {
      setForm(toInput(customer));
      setErrors({});
      setFromAnaf({});
    }
  }, [customer]);

  /**
   * Profilul de client se preselectează cu **primul din listă** (fondatorul, 2026-08-18).
   *
   * Nu e o comoditate: profilul decide reguli de facturare, iar „Fără profil" e o stare pe
   * care nimeni n-o alege intenționat — o obține uitând de câmp. Primul din listă e o
   * valoare pe care operatorul o vede și o poate schimba, spre deosebire de gol, pe care îl
   * vede doar cine se uită la fișă peste o lună.
   *
   * Se aplică și fișelor vechi rămase fără profil, nu doar la adăugare — de aceea depinde și
   * de `customer`: efectul care resetează formularul rulează înaintea ăstuia, iar fără
   * dependență am fi lăsat golul la loc după fiecare reîncărcare.
   */
  useEffect(() => {
    const first = (profiles.data ?? []).find((profile) => profile.status);
    if (!first) {
      return;
    }

    setForm((current) =>
      current.clientProfileId ? current : { ...current, clientProfileId: first.id },
    );
  }, [profiles.data, customer]);

  /**
   * Derularea la câmpul respins, după ce s-a randat.
   *
   * `useEffect`, nu un `scrollIntoView` chemat pe loc: refuzul poate veni cât timp e deschis
   * alt tab, iar conținutul lui „Detalii client" e demontat — elementul încă nu există în
   * pagină în clipa apăsării. Efectul rulează după commit, când există.
   */
  useEffect(() => {
    if (focusField === null) {
      return;
    }

    const element = document.getElementById(focusField);
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // `preventScroll`: derularea lină de mai sus e cea care contează, iar focus-ul ar sări
    // instant peste ea.
    element?.focus({ preventScroll: true });
    setFocusField(null);
  }, [focusField]);

  /**
   * Formularul, citit din afara randării.
   *
   * `applyAnaf` e chemată din promisiunea căutării, adică oricând până la timeout-ul de 5s
   * al serverului — timp în care operatorul poate să fi tastat în alt câmp. Închiderea
   * lexicală ar aduce cu ea formularul de la momentul apăsării și i-ar șterge, tăcut, ce a
   * scris între timp.
   */
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const set = <TField extends keyof CustomerInput>(field: TField, value: CustomerInput[TField]) => {
    setForm((current) => ({ ...current, [field]: value }));
    // Eroarea dispare la prima tastă din câmpul ei, nu abia la următoarea salvare: altfel
    // operatorul corectează CNP-ul și rămâne cu roșu sub el, fără să știe dacă a nimerit.
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    // La fel și propunerea de la ANAF: dacă a trecut prin câmp și a scris el, a ales.
    setFromAnaf((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  /**
   * Rezultatul căutării → formular.
   *
   * **Câmpurile goale se completează, cele scrise nu se suprascriu.** Aceeași regulă ca la
   * firmele grupului, din același motiv: operatorul care a tastat denumirea de mână și abia
   * apoi caută n-are cum să afle că i-a fost înlocuită. Ce diferă rămâne vizibil sub câmp, cu
   * un buton de înlocuit.
   *
   * Codul fiscal face excepție și se scrie direct: e chiar câmpul în care a căutat, iar
   * valoarea de la ANAF e forma normalizată a ce a tastat (fără `RO`, fără spații).
   */
  const applyAnaf = (found: CuiLookupCompany) => {
    const values: Record<AnafField, string> = {
      name: found.name,
      tradeRegisterNo: found.tradeRegisterNo ?? '',
      legalAddress: found.address.street ?? '',
      city: found.address.city ?? '',
      county: found.address.county ?? '',
      // ⚠️ Sondat pe viu (2026-08-14): firme mari vin fără cod poștal. Gol, nu inventat.
      postalCode: found.address.postalCode ?? '',
      // ANAF răspunde „da" sau „nu", niciodată „nu se știe": o căutare reușită **chiar**
      // stabilește starea de TVA, spre deosebire de un client tastat cu mâna.
      vatPayer: found.vatPayer ? 'da' : 'nu',
      vatOnCollection: found.vatOnCollection ? 'da' : 'nu',
      // La „Alt telefon", nu la cel de contact: numărul din registrul public e al firmei, nu
      // al persoanei cu care vorbește operatorul. Pus pe rolul greșit, ar fi sunat de cine
      // caută contabilitatea — și ar fi umplut un câmp obligatoriu cu o valoare pe care
      // nimeni n-a confirmat-o.
      phoneOther: found.phone ?? '',
    };

    const current = formRef.current;
    const filled: Partial<Record<AnafField, string>> = {};
    const different: Partial<Record<AnafField, string>> = {};

    for (const field of ANAF_FIELDS) {
      const value = values[field];
      if (!value) {
        // ANAF n-are câmpul (se întâmplă des). Nu golim ce a scris omul.
        continue;
      }

      if (current[field].trim().length === 0) {
        filled[field] = value;
      } else if (current[field].trim() !== value) {
        different[field] = value;
      }
    }

    setForm({ ...current, ...filled, cif: found.cui });
    setFromAnaf(different);
    setErrors((currentErrors) => ({ ...currentErrors, cif: undefined }));
  };

  /**
   * Refuzurile → sub câmpurile lor, cu operatorul dus la primul.
   *
   * Toate câmpurile obligatorii stau în „Detalii client", deci tabul se comută înapoi: un
   * mesaj roșu pe un tab închis nu e un mesaj.
   */
  const showErrors = (found: Partial<Record<keyof CustomerInput, string>>) => {
    setErrors(found);
    onTabChange('detalii');

    const first = Object.keys(found)[0] as keyof CustomerInput | undefined;
    setFocusField(first ? (FIELD_ANCHORS[first] ?? null) : null);
  };

  const mutation = useMutation({
    mutationFn: () => (customer ? updateCustomer(customer.id, form) : createCustomer(form)),
    onSuccess: async (saved) => {
      toast.success(customer ? 'Client actualizat' : 'Client adăugat');
      await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });

      if (customer) {
        await queryClient.invalidateQueries({ queryKey: customerQueryKey(customer.id) });
        return;
      }

      // După adăugare se merge pe fișă, nu înapoi în listă: locațiile se adaugă abia acum,
      // iar lista poate fi filtrată pe altă firmă decât cea aleasă — clientul tocmai salvat
      // ar părea că n-a fost salvat.
      await navigate({ to: '/clienti/$customerId', params: { customerId: saved.id } });
    },
    onError: (error: unknown) => {
      const fieldErrors = toFieldErrors(error, form);

      if (Object.keys(fieldErrors).length > 0) {
        // Motivul stă sub câmpul lui. **Fără toast** în cazul ăsta: două locuri care spun
        // același lucru, dintre care unul dispare singur, îl pun pe operator să caute.
        showErrors(fieldErrors);
        return;
      }

      toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');
    },
  });

  /**
   * Apăsarea pe Salvează.
   *
   * Butonul **rămâne apăsabil** când formularul e incomplet, iar asta e o schimbare de fond
   * față de cum era: un buton dezactivat nu spune ce lipsește. Cu fișele vechi — importate
   * fără e-mail sau fără persoană de contact — un `disabled` ar fi însemnat un ecran în care
   * apeși Salvează și nu se întâmplă absolut nimic, la fiecare client.
   */
  const submit = () => {
    const found = validate(form);

    if (Object.keys(found).length > 0) {
      showErrors(found);
      return;
    }

    mutation.mutate();
  };

  const descriptor =
    CUSTOMER_TYPE_LIST.find((type) => type.value === form.customerType) ?? CUSTOMER_TYPE_LIST[1];
  const isIndividual = form.customerType === CUSTOMER_TYPES.individual;
  const isForeign = form.customerType === CUSTOMER_TYPES.foreign;

  const disabled = mutation.isPending;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => onTabChange(customerTabSchema.parse(value))}
      className="flex flex-1 flex-col gap-4 sm:gap-6"
    >
      <SectionContainer>
        <SectionHeader
          title={
            customer ? (
              <span className="flex items-center gap-x-2">
                {customer.nickname}
                {/*
                  Marcaj pe fișă, nu doar în listă: fișa unui client arhivat se deschide și
                  din jurnal, sau dintr-un link primit — fără el ar arăta ca oricare alta,
                  iar întrebarea „de ce nu-l găsesc în listă" n-ar avea răspuns aici.
                */}
                {customer.archivedAt ? (
                  <Badge size="2xsmall" color="orange">
                    Arhivat
                  </Badge>
                ) : null}
              </span>
            ) : (
              'Client nou'
            )
          }
          /*
            Trei rânduri: codul (titlul), numele clientului, furnizorul (#222). Furnizorul e
            firma din grup care îl facturează — „pe care firmă e clientul ăsta" era întrebarea
            la care fișa răspundea doar în tabul de detalii, la câmpul „Firma din grup".
          */
          subtitle={
            customer ? (
              <span className="flex flex-col">
                <span>{customer.name}</span>
                {customer.companyName ? <span>Furnizor: {customer.companyName}</span> : null}
              </span>
            ) : undefined
          }
          actions={
            <>
              <Button size="small" variant="secondary" asChild>
                <Link to="/clienti">
                  <ArrowLeft />
                  Înapoi la listă
                </Link>
              </Button>
              {/* Doar pe o fișă salvată: un client care nu există încă n-are ce arhiva. */}
              {customer ? (
                /*
                  Roșu doar în sensul care închide: arhivarea reziliază abonamentele active
                  și închide custodiile (#111), deci nu poate arăta ca o acțiune neutră lipită
                  de „Salvează". Dezarhivarea nu scoate nimic din circulație — rămâne
                  secundară. Aceeași regulă ca pe butonul de confirmare din sertar, doar că
                  neutrul din antet e `secondary`: `primary` e al lui „Salvează".
                */
                <Button
                  size="small"
                  variant={isArchived ? 'secondary' : 'danger'}
                  onClick={() => setArchiveOpen(true)}
                  disabled={disabled}
                >
                  {isArchived ? <ArrowUturnLeft /> : <ArchiveBox />}
                  {isArchived ? 'Dezarhivează' : 'Arhivează'}
                </Button>
              ) : null}
              <Button size="small" onClick={submit} isLoading={mutation.isPending}>
                Salvează
              </Button>
            </>
          }
        />

        <div className="px-6 py-3">
          <Tabs.List>
            {CUSTOMER_TABS.map((item) => (
              <Tabs.Trigger key={item.value} value={item.value}>
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </div>
      </SectionContainer>

      <Tabs.Content value="detalii" className="flex flex-col gap-4 sm:gap-6">
        <SectionContainer>
          <SectionHeader title="Date de bază" />

          <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
            {/*
              Tipul și starea împart primul rând, singure. Tipul e comutatorul care schimbă
              restul formularului, iar starea e prima întrebare pe care o pune operatorul care
              deschide fișa — a stat până acum a patra într-o coloană de bife de facturare, cu
              care n-are nimic în comun.
            */}
            <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-start sm:justify-between">
              <Field label="Fel de client" id="client-tip" required>
                <Select
                  value={String(form.customerType)}
                  onValueChange={(value) => set('customerType', Number(value) as CustomerType)}
                  disabled={disabled}
                >
                  <Select.Trigger id="client-tip" className="sm:w-72">
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content className="z-50">
                    {CUSTOMER_TYPE_LIST.map((type) => (
                      <Select.Item key={type.value} value={String(type.value)}>
                        {type.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </Field>

              {/*
                Comutator, nu bifă: are două stări cu nume, iar cuvântul de lângă el spune pe
                care e acum. O bifă „Client activ" nebifată se citește la fel de ușor ca
                „am uitat s-o bifez".
              */}
              <Field label="Stare" id="client-activ">
                <div className="flex h-8 items-center gap-x-3">
                  <Switch
                    id="client-activ"
                    checked={form.status}
                    onCheckedChange={(value) => set('status', value)}
                    disabled={disabled}
                  />
                  <Text size="small" className={form.status ? undefined : 'text-ui-fg-subtle'}>
                    {form.status ? 'Activ' : 'Inactiv'}
                  </Text>
                </div>
              </Field>
            </div>

            {/*
              Eticheta poartă și numele din sistemul actual: coloana se cheamă `nickname`, iar
              operatorii care migrează caută cuvântul ăla, nu „cod client".
            */}
            <Field
              label="Cod client (nickname)"
              id="client-cod"
              required
              error={errors.nickname}
              hint="Unic, indiferent de majuscule."
            >
              <Input
                id="client-cod"
                value={form.nickname}
                onChange={(event) => set('nickname', event.target.value)}
                maxLength={60}
                aria-required
                aria-invalid={errors.nickname ? true : undefined}
                disabled={disabled}
              />
            </Field>

            <Field
              label={isIndividual ? 'Nume și prenume' : 'Denumire'}
              id="client-denumire"
              required
              error={errors.name}
              suggestion={
                fromAnaf.name
                  ? { value: fromAnaf.name, onApply: () => set('name', fromAnaf.name ?? '') }
                  : undefined
              }
            >
              <Input
                id="client-denumire"
                value={form.name}
                onChange={(event) => set('name', event.target.value)}
                maxLength={255}
                aria-required
                aria-invalid={errors.name ? true : undefined}
                disabled={disabled}
              />
            </Field>

            <Field label="Firma din grup" id="client-firma" required error={errors.companyId}>
              <Select
                value={form.companyId}
                onValueChange={(value) => set('companyId', value)}
                disabled={disabled}
              >
                <Select.Trigger id="client-firma">
                  <Select.Value placeholder="Alege firma" />
                </Select.Trigger>
                <Select.Content className="z-50">
                  {(companies.data ?? [])
                    .filter((company) => company.status || company.id === form.companyId)
                    .map((company) => (
                      <Select.Item key={company.id} value={company.id}>
                        {companyDisplayName(company)}
                      </Select.Item>
                    ))}
                </Select.Content>
              </Select>
            </Field>

            <Field
              label="Profil de client"
              id="client-profil"
              error={errors.clientProfileId}
              hint="Se administrează în Setări."
            >
              <Select
                value={form.clientProfileId}
                onValueChange={(value) => set('clientProfileId', value)}
                disabled={disabled}
              >
                <Select.Trigger id="client-profil">
                  <Select.Value placeholder="Fără profil" />
                </Select.Trigger>
                <Select.Content className="z-50">
                  {(profiles.data ?? [])
                    .filter((profile) => profile.status || profile.id === form.clientProfileId)
                    .map((profile) => (
                      <Select.Item key={profile.id} value={profile.id}>
                        {profile.name}
                      </Select.Item>
                    ))}
                </Select.Content>
              </Select>
            </Field>
          </div>
        </SectionContainer>

        <SectionContainer>
          <SectionHeader
            title="Identificare fiscală"
            subtitle={
              hasAnafLookup(form.customerType)
                ? 'Căutarea la ANAF completează doar câmpurile goale.'
                : undefined
            }
          />

          <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
            {isIndividual ? (
              <>
                <Field label="CNP" id="client-cnp" error={errors.cnp}>
                  <Input
                    id="client-cnp"
                    value={form.cnp}
                    onChange={(event) => set('cnp', event.target.value)}
                    maxLength={13}
                    className="font-mono"
                    autoComplete="off"
                    aria-invalid={errors.cnp ? true : undefined}
                    disabled={disabled}
                  />
                </Field>

                <Field label="Serie și număr CI" id="client-ci" error={errors.idCardNumber}>
                  <Input
                    id="client-ci"
                    value={form.idCardNumber}
                    onChange={(event) => set('idCardNumber', event.target.value)}
                    maxLength={30}
                    autoComplete="off"
                    disabled={disabled}
                  />
                </Field>
              </>
            ) : (
              <>
                <div className={hasAnafLookup(form.customerType) ? 'sm:col-span-2' : undefined}>
                  <Field
                    label={descriptor?.fiscalIdLabel ?? 'Cod fiscal'}
                    id="client-cif"
                    required
                    error={errors.cif}
                  >
                    {hasAnafLookup(form.customerType) ? (
                      /*
                      Aceeași componentă ca la firmele grupului (#52), cu alt `onResult`:
                      apelul stă în aplicație, iar maparea pe câmpuri e a ecranului. La firma
                      străină nu se arată deloc — ANAF nu știe de firme bulgărești, iar un
                      buton care întoarce mereu „nu există" e mai rău decât lipsa lui.
                    */
                      <CuiLookup
                        id="client-cif"
                        value={form.cif}
                        onChange={(next) => set('cif', next)}
                        onLookup={lookupCompanyByCui}
                        onResult={applyAnaf}
                        disabled={disabled}
                        required
                        invalid={errors.cif !== undefined}
                      />
                    ) : (
                      <Input
                        id="client-cif"
                        value={form.cif}
                        onChange={(event) => set('cif', event.target.value.toUpperCase())}
                        maxLength={30}
                        className="font-mono"
                        aria-required
                        aria-invalid={errors.cif ? true : undefined}
                        disabled={disabled}
                      />
                    )}
                  </Field>
                </div>

                <Field
                  label={isForeign ? 'Nr. de înregistrare' : 'Nr. Reg. Com.'}
                  id="client-registru"
                  error={errors.tradeRegisterNo}
                  suggestion={
                    fromAnaf.tradeRegisterNo
                      ? {
                          value: fromAnaf.tradeRegisterNo,
                          onApply: () => set('tradeRegisterNo', fromAnaf.tradeRegisterNo ?? ''),
                        }
                      : undefined
                  }
                >
                  <Input
                    id="client-registru"
                    value={form.tradeRegisterNo}
                    onChange={(event) => set('tradeRegisterNo', event.target.value)}
                    maxLength={30}
                    disabled={disabled}
                  />
                </Field>

                {isForeign ? (
                  <Field
                    label="Țară"
                    id="client-tara"
                    required
                    error={errors.countryCode}
                    hint="Cod ISO, două litere: BG, HU, DE."
                  >
                    <Input
                      id="client-tara"
                      value={form.countryCode}
                      onChange={(event) => set('countryCode', event.target.value.toUpperCase())}
                      maxLength={2}
                      className="w-20 font-mono"
                      aria-required
                      aria-invalid={errors.countryCode ? true : undefined}
                      disabled={disabled}
                    />
                  </Field>
                ) : null}
              </>
            )}
          </div>
        </SectionContainer>

        {!isIndividual ? (
          <SectionContainer>
            <SectionHeader title="Reprezentant legal" />
            <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
              {/* Fără nume, contractul n-are cine să-l semneze — de aceea e obligatoriu, iar
                calitatea nu: la tipărire are un implicit rezonabil. */}
              <Field
                label="Nume"
                id="client-reprezentant"
                required
                error={errors.representativeName}
              >
                <Input
                  id="client-reprezentant"
                  value={form.representativeName}
                  onChange={(event) => set('representativeName', event.target.value)}
                  maxLength={255}
                  aria-required
                  aria-invalid={errors.representativeName ? true : undefined}
                  disabled={disabled}
                />
              </Field>

              <Field
                label="Calitate"
                id="client-calitate"
                error={errors.representativeRole}
                hint="Administrator, Director general, …"
              >
                <Input
                  id="client-calitate"
                  value={form.representativeRole}
                  onChange={(event) => set('representativeRole', event.target.value)}
                  maxLength={100}
                  disabled={disabled}
                />
              </Field>
            </div>
          </SectionContainer>
        ) : null}

        <SectionContainer>
          <SectionHeader title={isIndividual ? 'Domiciliu' : 'Sediu social'} />
          <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field
                label="Adresă"
                id="client-adresa"
                error={errors.legalAddress}
                suggestion={
                  fromAnaf.legalAddress
                    ? {
                        value: fromAnaf.legalAddress,
                        onApply: () => set('legalAddress', fromAnaf.legalAddress ?? ''),
                      }
                    : undefined
                }
              >
                <Input
                  id="client-adresa"
                  value={form.legalAddress}
                  onChange={(event) => set('legalAddress', event.target.value)}
                  maxLength={300}
                  disabled={disabled}
                />
              </Field>
            </div>

            <Field
              label="Localitate"
              id="client-localitate"
              error={errors.city}
              suggestion={
                fromAnaf.city
                  ? { value: fromAnaf.city, onApply: () => set('city', fromAnaf.city ?? '') }
                  : undefined
              }
            >
              <Input
                id="client-localitate"
                value={form.city}
                onChange={(event) => set('city', event.target.value)}
                maxLength={100}
                disabled={disabled}
              />
            </Field>

            {/* Județul lipsește la firmele străine: nu e o împărțire administrativă care să
              existe acolo, iar serverul golește coloana oricum. */}
            {isForeign ? null : (
              <Field
                label="Județ"
                id="client-judet"
                error={errors.county}
                suggestion={
                  fromAnaf.county
                    ? {
                        value: fromAnaf.county,
                        onApply: () => set('county', fromAnaf.county ?? ''),
                      }
                    : undefined
                }
              >
                <Input
                  id="client-judet"
                  value={form.county}
                  onChange={(event) => set('county', event.target.value)}
                  maxLength={60}
                  disabled={disabled}
                />
              </Field>
            )}

            <Field
              label="Cod poștal"
              id="client-cod-postal"
              error={errors.postalCode}
              suggestion={
                fromAnaf.postalCode
                  ? {
                      value: fromAnaf.postalCode,
                      onApply: () => set('postalCode', fromAnaf.postalCode ?? ''),
                    }
                  : undefined
              }
            >
              <Input
                id="client-cod-postal"
                value={form.postalCode}
                onChange={(event) => set('postalCode', event.target.value)}
                // 10, nu 6: coloana ține și coduri străine, de forma `SW1A 1AA`.
                maxLength={10}
                disabled={disabled}
              />
            </Field>
          </div>
        </SectionContainer>

        {/* TVA-ul nu apare la persoane fizice: nu sunt înregistrate în scopuri de TVA, iar
          un câmp gol acolo ar fi o întrebare fără răspuns posibil. */}
        {isIndividual ? null : (
          <SectionContainer>
            <SectionHeader title="TVA" />
            <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
              <Field
                label="Plătitor de TVA"
                id="client-tva"
                error={errors.vatPayer}
                suggestion={
                  fromAnaf.vatPayer
                    ? {
                        value: fromAnaf.vatPayer === 'da' ? 'Da' : 'Nu',
                        onApply: () => set('vatPayer', fromAnaf.vatPayer ?? ''),
                      }
                    : undefined
                }
              >
                <Select
                  value={form.vatPayer}
                  onValueChange={(value) => set('vatPayer', value)}
                  disabled={disabled}
                >
                  <Select.Trigger id="client-tva">
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content className="z-50">
                    {TRISTATE_OPTIONS.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </Field>

              <Field
                label="TVA la încasare"
                id="client-tva-incasare"
                error={errors.vatOnCollection}
                suggestion={
                  fromAnaf.vatOnCollection
                    ? {
                        value: fromAnaf.vatOnCollection === 'da' ? 'Da' : 'Nu',
                        onApply: () => set('vatOnCollection', fromAnaf.vatOnCollection ?? ''),
                      }
                    : undefined
                }
              >
                <Select
                  value={form.vatOnCollection}
                  onValueChange={(value) => set('vatOnCollection', value)}
                  disabled={disabled}
                >
                  <Select.Trigger id="client-tva-incasare">
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content className="z-50">
                    {TRISTATE_OPTIONS.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </Field>
            </div>
          </SectionContainer>
        )}

        <SectionContainer>
          <SectionHeader title="Contact" />
          <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
            {/* Obligatoriu la toate felurile de client: fără adresă nu se poate trimite
              factura, iar fișa ar arăta completă fără să fie. */}
            <Field label="E-mail" id="client-email" required error={errors.email}>
              <Input
                id="client-email"
                type="email"
                value={form.email}
                onChange={(event) => set('email', event.target.value)}
                maxLength={190}
                aria-required
                aria-invalid={errors.email ? true : undefined}
                disabled={disabled}
              />
            </Field>

            {/*
              „Telefon contact", nu „Telefon vânzări": e numărul la care se sună clientul,
              oricare ar fi motivul. Coloana a rămas `phone_sales` — o migrare pentru un
              cuvânt n-ar cumpăra nimic, iar sistemul actual scrie tot acolo.
            */}
            <Field
              label="Telefon contact"
              id="client-tel-contact"
              required={!isIndividual}
              error={errors.phoneSales}
            >
              <Input
                id="client-tel-contact"
                value={form.phoneSales}
                onChange={(event) => set('phoneSales', event.target.value)}
                maxLength={30}
                aria-required={isIndividual ? undefined : true}
                aria-invalid={errors.phoneSales ? true : undefined}
                disabled={disabled}
              />
            </Field>

            <Field label="Telefon facturare" id="client-tel-facturare" error={errors.phoneBilling}>
              <Input
                id="client-tel-facturare"
                value={form.phoneBilling}
                onChange={(event) => set('phoneBilling', event.target.value)}
                maxLength={30}
                disabled={disabled}
              />
            </Field>

            {/*
              Mai multe adrese, separate prin virgulă — se normalizează la salvare, deci
              câmpul arată la reîncărcare ce s-a înțeles din ce a tastat operatorul.
              **Nu ține locul lui „E-mail"**: acolo pleacă linkul de semnare, care are
              nevoie de o singură adresă. Fără hint sub câmp — eticheta e de ajuns.
            */}
            <Field label="E-mail facturare" id="client-email-facturare" error={errors.billingEmail}>
              <Input
                id="client-email-facturare"
                value={form.billingEmail}
                onChange={(event) => set('billingEmail', event.target.value)}
                maxLength={2000}
                aria-invalid={errors.billingEmail ? true : undefined}
                disabled={disabled}
              />
            </Field>

            <Field label="Telefon tehnic" id="client-tel-tehnic" error={errors.phoneTechnical}>
              <Input
                id="client-tel-tehnic"
                value={form.phoneTechnical}
                onChange={(event) => set('phoneTechnical', event.target.value)}
                maxLength={30}
                disabled={disabled}
              />
            </Field>

            <Field
              label="Alt telefon"
              id="client-tel-alt"
              error={errors.phoneOther}
              suggestion={
                fromAnaf.phoneOther
                  ? {
                      value: fromAnaf.phoneOther,
                      onApply: () => set('phoneOther', fromAnaf.phoneOther ?? ''),
                    }
                  : undefined
              }
            >
              <Input
                id="client-tel-alt"
                value={form.phoneOther}
                onChange={(event) => set('phoneOther', event.target.value)}
                maxLength={30}
                disabled={disabled}
              />
            </Field>

            <div className="sm:col-span-2">
              <Field
                label="Persoană de contact"
                id="client-contact"
                required={!isIndividual}
                error={errors.contactPerson}
                hint={isIndividual ? undefined : 'Semnatarul se completează la Reprezentant legal.'}
              >
                <Textarea
                  id="client-contact"
                  value={form.contactPerson}
                  onChange={(event) => set('contactPerson', event.target.value)}
                  rows={2}
                  aria-required={isIndividual ? undefined : true}
                  aria-invalid={errors.contactPerson ? true : undefined}
                  disabled={disabled}
                />
              </Field>
            </div>
          </div>
        </SectionContainer>

        <SectionContainer>
          <SectionHeader title="Bancă și facturare" />
          <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
            <Field label="IBAN" id="client-iban" error={errors.iban}>
              <Input
                id="client-iban"
                value={form.iban}
                onChange={(event) => set('iban', event.target.value.toUpperCase())}
                maxLength={42}
                className="font-mono"
                aria-invalid={errors.iban ? true : undefined}
                disabled={disabled}
              />
            </Field>

            <Field label="Bancă" id="client-banca" error={errors.bankName}>
              <Input
                id="client-banca"
                value={form.bankName}
                onChange={(event) => set('bankName', event.target.value)}
                maxLength={100}
                disabled={disabled}
              />
            </Field>

            <div className="sm:col-span-2">
              <Field
                label="Denumiri în extrasul de cont"
                id="client-extras"
                error={errors.bankStatementNames}
                hint="Separate prin virgulă."
              >
                <Textarea
                  id="client-extras"
                  value={form.bankStatementNames}
                  onChange={(event) => set('bankStatementNames', event.target.value)}
                  rows={2}
                  disabled={disabled}
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="Note" id="client-note" error={errors.notes}>
                <Textarea
                  id="client-note"
                  value={form.notes}
                  onChange={(event) => set('notes', event.target.value)}
                  rows={3}
                  disabled={disabled}
                />
              </Field>
            </div>

            {/*
              Bifele de facturare vin din sistemul actual și se migrează. Din Faza 2 au și
              consumator — indiciul de sub fiecare spune unde, ca operatorul să știe ce schimbă:
              `auto_invoicing` dă bifa implicită din Conta → Facturare automată (#184),
              `detailed_invoice` liniile facturii lunii, în lot și în fișă (#186), iar „special"
              scoate clientul din lista aceea (#205): se facturează de aici, din fișă.
            */}
            <div className="flex flex-col gap-y-3 sm:col-span-2">
              <ToggleField
                id="client-auto-facturare"
                label="Facturare automată"
                hint="Bifat implicit în Conta → Facturare automată."
                checked={form.autoInvoicing}
                onChange={(value) => set('autoInvoicing', value)}
                disabled={disabled}
              />
              <ToggleField
                id="client-factura-detaliata"
                label="Factură detaliată"
                hint={'O linie pe abonament. Altfel, o singură linie: „Servicii".'}
                checked={form.detailedInvoice}
                onChange={(value) => set('detailedInvoice', value)}
                disabled={disabled}
              />
              <ToggleField
                id="client-special"
                label="Client special"
                hint="Nu apare în Conta → Facturare automată."
                checked={form.isSpecial}
                onChange={(value) => set('isSpecial', value)}
                disabled={disabled}
              />
            </div>
          </div>
        </SectionContainer>
      </Tabs.Content>

      {/*
        Registrul de documente al clientului: contracte-cadru și anexe, cu numerele din
        registrul firmei lui. Numărul se tipărește ca atare — vezi
        docs/wiki/domains/document-manager.md#numerotarea

        ⚠️ **Și semnarea, de la #115.** A avut tab propriu între 2026-09-01 și 2026-09-15;
        a plecat pentru că operatorul nu se uită la sesiuni, ci la documente: starea fiecărui
        act e aici, iar trimiterea la semnat e o acțiune pe rândul lui.
      */}
      <Tabs.Content value="documente" className="flex flex-col gap-4 sm:gap-6">
        <CustomerDocuments customerId={customer?.id ?? null} archived={isArchived} />
      </Tabs.Content>

      {/*
        Serviciile clientului. Avea deasupra un card de locații; a plecat odată cu tabelul
        (fondatorul, 2026-08-18) — adresa de instalare e acum un câmp pe abonament, pentru
        că acolo se citește: la generarea anexei.
      */}
      <Tabs.Content value="servicii" className="flex flex-col gap-4 sm:gap-6">
        <CustomerSubscriptions customerId={customer?.id ?? null} archived={isArchived} />
      </Tabs.Content>

      {/*
        Facturile clientului și „Emite factură" (#186). Primește clientul **salvat**: formularul
        facturii arată datele din fișă, nu ce s-a tastat între timp și încă n-a fost salvat — pe
        factură se îngheață ce e în bază.
      */}
      <Tabs.Content value="financiar">
        <CustomerBilling customer={customer} />
      </Tabs.Content>

      {/*
        Sertarul primește clientul **salvat**, nu formularul: arhivarea e o acțiune asupra
        rândului din bază, nu asupra a ce s-a tastat între timp și încă n-a fost salvat.
      */}
      <ArchiveCustomerDrawer
        customer={archiveOpen ? customer : null}
        onClose={() => setArchiveOpen(false)}
      />
    </Tabs>
  );
}

function ToggleField({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-x-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        disabled={disabled}
      />
      <div className="flex flex-col gap-y-1">
        <Label htmlFor={id} weight="plus">
          {label}
        </Label>
        {hint ? (
          <Text size="small" className="text-ui-fg-subtle">
            {hint}
          </Text>
        ) : null}
      </div>
    </div>
  );
}
